import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, ReferenceLine,
  XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from 'recharts';
import { API } from '../../config';

/* Quante verifiche di copertura scadranno, e quando.

   Le verifiche sono partite a lotti, quindi tornano a lotti: la coda non cresce piano,
   arriva a ondate. I cursori SIMULANO timing diversi senza salvare niente: si vede
   l'effetto prima di decidere, invece di cambiare al buio e scoprirlo dopo. */

const nf = (n) => (n || 0).toLocaleString('it-IT');
const gg = (d) => (d || '').slice(8) + '/' + (d || '').slice(5, 7);
const FASCE = [
  { k: 'monitor', l: 'Monitor', col: '#e0662e', nota: 'FWA, FTTC e ADSL: i candidati a diventare FTTH' },
  { k: 'resto', l: 'Resto', col: '#3283bd', nota: 'Non censiti, non coperti, errori' },
  { k: 'ftth', l: 'FTTH', col: '#14746f', nota: 'Già al massimo: si ricontrolla di rado' },
];
const LIMITI = { monitor: [7, 180], resto: [7, 365], ftth: [30, 2000] };

export default function TabProiezione() {
  const [dati, setDati] = useState(null);
  const [scoperte, setScoperte] = useState(null);
  const [errore, setErrore] = useState(false);
  const [orizzonte, setOrizzonte] = useState(90);
  const [sim, setSim] = useState(null);        // timing dei cursori (null = quelli in vigore)
  const [inVigore, setInVigore] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const req = useRef(0);

  const carica = useCallback((timing, giorni) => {
    const my = ++req.current;
    const p = new URLSearchParams({ giorni: String(giorni) });
    if (timing) Object.entries(timing).forEach(([k, v]) => p.set(k, String(v)));
    axios.get(`${API}/copertura/proiezione?${p}`)
      .then((r) => {
        if (my !== req.current) return;
        setDati(r.data); setErrore(false);
        if (!timing) { setSim(r.data.timing); setInVigore(r.data.timing); }
      })
      .catch(() => { if (my === req.current) { setErrore(true); setDati(null); } });
  }, []);

  useEffect(() => { carica(null, orizzonte); }, []);          // eslint-disable-line
  // le scoperte non dipendono dai cursori: si leggono una volta sola
  useEffect(() => {
    axios.get(`${API}/copertura/scoperte-ftth`).then((r) => setScoperte(r.data)).catch(() => {});
  }, []);
  useEffect(() => { if (sim) carica(sim, orizzonte); }, [sim, orizzonte]); // eslint-disable-line

  if (errore) return <div className="cop-empty">Non riesco a calcolare la proiezione.</div>;
  if (!dati || !sim) return <div className="cop-empty">Calcolo la proiezione…</div>;

  const cambiato = inVigore && FASCE.some((f) => sim[f.k] !== inVigore[f.k]);
  const applica = async () => {
    setSalvando(true);
    try {
      await axios.put(`${API}/settings`, {
        copertura_timing_monitor: sim.monitor,
        copertura_timing_giorni: sim.resto,
        copertura_timing_ftth: sim.ftth,
      });
      setInVigore(sim);
      toast.success('Timing aggiornati: valgono dal prossimo giro dell\'agente');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    } finally { setSalvando(false); }
  };

  const serie = dati.giorni.map((r) => ({ ...r, giorno: gg(r.data) }));
  const smalt = dati.smaltimento_giorni;
  const picco = serie.reduce((m, r) => (r.totale > (m?.totale || 0) ? r : m), null);
  const regge = dati.ritmo_giorno > 0 && dati.carico.al_giorno <= dati.ritmo_giorno;

  return (
    <div className="cop">
      <div className="cop-kpis">
        <div className="cop-kpi">
          <div className="cop-kpi-v">{nf(dati.arretrato)}</div>
          <div className="cop-kpi-l">Da fare adesso</div>
          <div className="cop-kpi-s">{dati.mai_verificati ? `di cui ${nf(dati.mai_verificati)} mai verificati` : 'nessun mai verificato'}</div>
        </div>
        <div className="cop-kpi">
          <div className="cop-kpi-v">{nf(dati.totale)}</div>
          <div className="cop-kpi-l">Entro {orizzonte} giorni</div>
          <div className="cop-kpi-s">arretrato + quel che matura</div>
        </div>
        <div className="cop-kpi">
          <div className="cop-kpi-v">{nf(dati.carico.al_giorno)}</div>
          <div className="cop-kpi-l">Al giorno, a regime</div>
          <div className="cop-kpi-s">{nf(dati.carico.totale)} all'anno</div>
        </div>
        <div className="cop-kpi">
          <div className="cop-kpi-v" style={{ color: regge ? '#16a34a' : '#b45309' }}>{dati.ritmo_giorno ? nf(dati.ritmo_giorno) : '—'}</div>
          <div className="cop-kpi-l">Ritmo dell'agente</div>
          <div className="cop-kpi-s">
            {dati.ritmo_giorno
              ? `media sui ${dati.giorni_lavorati_30} giorni in cui ha lavorato`
              : 'non ha lavorato negli ultimi 30 giorni'}
          </div>
        </div>
      </div>

      {/* Perche' la fascia monitor esiste: ogni tanto una di quelle aziende guadagna la
          fibra, e sono i lead migliori. Il conto sta accanto alla previsione del carico,
          cosi' si vede cosa si compra con quelle verifiche. */}
      {scoperte && scoperte.totale > 0 && (
        <div className="cop-card">
          <div className="cop-card-h">Nuove FTTH trovate finora</div>
          <div className="cop-cols">
            <div>
              <div style={{ display: 'flex', gap: 26, flexWrap: 'wrap', marginBottom: 12 }}>
                <div>
                  <div className="cop-kpi-v" style={{ color: '#14746f' }}>{nf(scoperte.totale)}</div>
                  <div className="cop-kpi-l">Aziende passate a FTTH</div>
                </div>
                <div>
                  <div className="cop-kpi-v" style={{ color: '#e0662e' }}>{nf(scoperte.non_tim)}</div>
                  <div className="cop-kpi-l">di cui NON TIM</div>
                  <div className="cop-kpi-s">i lead veri</div>
                </div>
                <div>
                  <div className="cop-kpi-v">{scoperte.tasso}%</div>
                  <div className="cop-kpi-l">Resa dei ricontrolli</div>
                  <div className="cop-kpi-s">su {nf(scoperte.ricontrollati)} negli ultimi {scoperte.finestra_giorni} giorni</div>
                </div>
              </div>
              <div className="cop-note">
                {scoperte.nel_pool > 0
                  ? <>Nel pool esportabile ne restano <b>{nf(scoperte.nel_pool)}</b>; le altre sono già uscite.</>
                  : <>Sono già finite tutte nell'export.</>}
                {scoperte.da_guardare > 0 && <> <b>{nf(scoperte.da_guardare)}</b> hanno ancora il badge NEW mai aperto.</>}
              </div>
            </div>
            <div>
              {scoperte.per_origine.map((o) => (
                <div key={o.da}>
                  <div className="cop-bar-l">
                    <span style={{ fontWeight: 600 }}>da {o.da}</span>
                    <span className="cop-bar-n">{nf(o.saliti)} su {nf(o.ricontrollati)} · {o.tasso}%</span>
                  </div>
                  <div className="cop-bar"><i style={{ width: `${Math.min(100, o.tasso * 10)}%`, background: '#14746f' }} /></div>
                </div>
              ))}
              <div className="cop-note">Quota di chi, ricontrollato, ha guadagnato la fibra.</div>
              {dati.composizione.monitor > 0 && scoperte.tasso > 0 && (
                <div className="cop-note" style={{ marginTop: 10, color: '#14746f', fontWeight: 600 }}>
                  Sui {nf(dati.composizione.monitor)} sotto osservazione, a questa resa il prossimo
                  giro completo dovrebbe portarne circa {nf(Math.round(dati.composizione.monitor * scoperte.tasso / 100))}.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="cop-card">
        <div className="cop-card-h">Simulazione — i cursori non salvano niente</div>
        <div className="cop-sim">
          {FASCE.map((f) => (
            <div key={f.k}>
              <div className="cop-sim-h">
                <label htmlFor={`sim-${f.k}`}>{f.l}</label>
                <span className="cop-sim-v">{sim[f.k]} gg</span>
              </div>
              <input id={`sim-${f.k}`} type="range" min={LIMITI[f.k][0]} max={LIMITI[f.k][1]} value={sim[f.k]}
                onChange={(e) => setSim({ ...sim, [f.k]: Number(e.target.value) })} />
              <div className="cop-sim-nota">{f.nota}{inVigore && sim[f.k] !== inVigore[f.k] ? ` · in vigore ${inVigore[f.k]}` : ''}</div>
            </div>
          ))}
          <div>
            <div className="cop-sim-h">
              <label htmlFor="sim-oriz">Orizzonte</label>
              <span className="cop-sim-v">{orizzonte} gg</span>
            </div>
            <input id="sim-oriz" type="range" min={14} max={365} step={7} value={orizzonte}
              onChange={(e) => setOrizzonte(Number(e.target.value))} />
            <div className="cop-sim-nota">fin dove guardare avanti</div>
          </div>
        </div>
        <div className="cop-sim-azioni" style={{ marginTop: 14 }}>
          {cambiato
            ? <>
              <span className="cop-diverso">Stai guardando una configurazione diversa da quella in vigore.</span>
              <button className="btn btn-primary" onClick={applica} disabled={salvando}>
                {salvando ? 'Salvo…' : 'Applica questi timing'}
              </button>
              <button className="btn" onClick={() => setSim(inVigore)}>Torna a quelli in vigore</button>
            </>
            : <span className="cop-note">Questi sono i timing in vigore. Muovi i cursori per vedere come cambierebbe il carico.</span>}
        </div>
      </div>

      <div className="cop-card">
        <div className="cop-card-h">Verifiche che maturano, giorno per giorno</div>
        {serie.length === 0 ? (
          <div className="cop-empty">Con questi timing non scade niente nei prossimi {orizzonte} giorni.</div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={serie} margin={{ top: 6, right: 8, left: -14, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-card, #e2e8f0)" vertical={false} />
              <XAxis dataKey="giorno" tick={{ fontSize: 10, fill: 'var(--text-secondary, #64748b)' }} interval="preserveStartEnd" />
              <YAxis yAxisId="g" tick={{ fontSize: 10, fill: 'var(--text-secondary, #64748b)' }} width={44} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 10 }} formatter={(v, n) => [nf(v), n]} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {dati.ritmo_giorno > 0 && (
                <ReferenceLine yAxisId="g" y={dati.ritmo_giorno} stroke="#94a3b8" strokeDasharray="5 4"
                  label={{ value: `capacità ${nf(dati.ritmo_giorno)}/gg`, position: 'insideTopRight', fontSize: 10, fill: '#64748b' }} />
              )}
              {FASCE.map((f) => (
                <Bar key={f.k} yAxisId="g" dataKey={f.k} name={f.l} stackId="a" fill={f.col} />
              ))}
              <Line yAxisId="g" type="monotone" dataKey="coda" name="Coda accumulata" stroke="#b45309" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
        <div className="cop-note" style={{ marginTop: 8 }}>
          Le barre sono le verifiche che scadono quel giorno; la linea è la coda che si accumula
          se non venisse smaltita. La tratteggiata è il ritmo reale dell'agente: dove la linea
          la supera, la coda cresce.
        </div>
      </div>

      <div className="cop-cols">
        <div className="cop-card">
          <div className="cop-card-h">Cosa dicono i numeri</div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.7 }}>
            <li>Sotto osservazione: <b>{nf(dati.composizione.monitor)}</b> fra FWA, FTTC e ADSL — sono quelli che possono diventare FTTH.</li>
            {picco && <li>Giorno più carico: <b>{picco.data.split('-').reverse().join('/')}</b> con <b>{nf(picco.totale)}</b> verifiche in un colpo.</li>}
            <li>
              {smalt == null
                ? (dati.ritmo_giorno ? 'A questo ritmo la coda non si richiude: il carico supera la capacità.' : 'Senza un ritmo misurato non posso dire quando la coda si chiude.')
                : smalt === 0 ? 'Non c\'è niente in coda.'
                  : <>La coda si richiude fra <b>{smalt} giorni</b>, al ritmo attuale.</>}
            </li>
            <li>A regime servono <b>{nf(dati.carico.al_giorno)}</b> verifiche al giorno{dati.ritmo_giorno ? <> contro <b>{nf(dati.ritmo_giorno)}</b> di capacità</> : ''}.</li>
          </ul>
        </div>
        <div className="cop-card">
          <div className="cop-card-h">Carico annuo per fascia</div>
          {FASCE.map((f) => {
            const v = dati.carico.per_fascia[f.k] || 0;
            const pct = dati.carico.totale ? Math.round((v / dati.carico.totale) * 100) : 0;
            return (
              <div key={f.k}>
                <div className="cop-bar-l">
                  <span style={{ fontWeight: 600 }}>{f.l} <span className="cop-note">· {nf(dati.composizione[f.k])} record ogni {sim[f.k]} gg</span></span>
                  <span className="cop-bar-n">{nf(v)} · {pct}%</span>
                </div>
                <div className="cop-bar"><i style={{ width: `${pct}%`, background: f.col }} /></div>
              </div>
            );
          })}
        </div>
      </div>

      {serie.length > 0 && (
        <div className="cop-tablewrap">
          <table className="cop-table">
            <thead>
              <tr>
                <th className="l">Data</th><th>Monitor</th><th>Resto</th><th>FTTH</th><th>Totale</th><th>Coda</th>
              </tr>
            </thead>
            <tbody>
              <tr className="oggi">
                <td className="l">oggi</td><td>—</td><td>—</td><td>—</td>
                <td>{nf(dati.arretrato)}</td><td className="strong">{nf(dati.arretrato)}</td>
              </tr>
              {dati.giorni.map((r) => (
                <tr key={r.data}>
                  <td className="l">{r.data.split('-').reverse().join('/')}</td>
                  <td>{r.monitor ? nf(r.monitor) : '—'}</td>
                  <td>{r.resto ? nf(r.resto) : '—'}</td>
                  <td>{r.ftth ? nf(r.ftth) : '—'}</td>
                  <td>{nf(r.totale)}</td>
                  <td className="strong">{nf(r.coda)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
